import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, useDraggable, useDroppable,
} from "@dnd-kit/core";
import { colors, font, formatCurrency, formatDate, generateId } from "../utils/theme.js";
import { api } from "../api/client.js";
import {
  ACTIVE_STAGES, OUTCOME_STAGES, STAGE_LABELS, OUTCOME_COLORS, isPreSubmission, DEFAULT_NEW_STAGE,
} from "./stages.js";
import { dueUrgency, URGENCY_STYLE } from "./urgency.js";
import { computeMetrics } from "./metrics.js";
import MultiSelect from "./MultiSelect.jsx";
import DetailPanel from "./DetailPanel.jsx";

const EMPTY_FILTERS = { client: [], projectManager: [], accountOwner: [] };

const COL_W = 264;

export default function ProposalBoard({ user, users }) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [clients, setClients] = useState([]); // client registry [{id, name}]
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const admins = useMemo(() => (users || []).filter((u) => u.role === "admin"), [users]);

  const collapseKey = `proposalBoard.outcomeCollapsed.${user?.email || "anon"}`;

  useEffect(() => {
    try { setCollapsed(localStorage.getItem(collapseKey) === "1"); } catch { /* ignore */ }
  }, [collapseKey]);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(collapseKey, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await api.proposals.list();
      setProposals(Array.isArray(data) ? data : []);
      // Client registry feeds the picker and resolves clientId → name. Non-fatal:
      // if it fails, legacy `client` strings still render and filter.
      api.clients.list()
        .then((c) => setClients(Array.isArray(c) ? c : []))
        .catch(() => setClients([]));
    } catch (e) {
      setError(e.message || "Could not load proposals.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flashToast = (type, text) => {
    setToast({ type, text });
    // best-effort auto-dismiss; not load-bearing
    setTimeout(() => setToast((t) => (t && t.text === text ? null : t)), 6000);
  };

  const byId = useCallback((id) => proposals.find((p) => p.id === id), [proposals]);

  // Resolve a proposal's client for display/filter: registry name by clientId, else legacy string.
  const clientName = useCallback((p) => clients.find((c) => c.id === p.clientId)?.name || p.client || "", [clients]);

  // Guarded stage change: optimistic move, rollback on failure, refetch on conflict.
  const moveStage = async (id, toStage) => {
    const card = byId(id);
    if (!card || card.stage === toStage) return { ok: true };
    const snapshot = card;
    setProposals((list) => list.map((p) => (p.id === id ? { ...p, stage: toStage } : p)));
    const res = await api.proposals.setStage(id, toStage, card._etag, user?.email);
    if (res.ok) {
      setProposals((list) => list.map((p) => (p.id === id ? res.data : p)));
      return { ok: true };
    }
    if (res.status === 409) {
      flashToast("error", res.data?.error || "That proposal changed elsewhere — board refreshed.");
      await load(); // refetch truth; discards the optimistic move
      return { ok: false, error: res.data?.error };
    }
    setProposals((list) => list.map((p) => (p.id === id ? snapshot : p))); // revert
    flashToast("error", res.data?.error || "Move failed — card reverted.");
    return { ok: false, error: res.data?.error };
  };

  const saveFields = async (id, fields) => {
    const card = byId(id);
    if (!card) return { ok: false, error: "not found" };
    const res = await api.proposals.updateFields(id, fields, card._etag);
    if (res.ok) { setProposals((list) => list.map((p) => (p.id === id ? res.data : p))); return { ok: true }; }
    if (res.status === 409) { flashToast("error", res.data?.error || "Changed elsewhere — refreshed."); await load(); }
    return { ok: false, error: res.data?.error || "Save failed." };
  };

  const createProposal = async (record) => {
    const now = new Date().toISOString();
    const stage = record.stage || DEFAULT_NEW_STAGE;
    const doc = {
      ...record, id: generateId(), stage,
      stageHistory: [{ stage, enteredAt: now, changedBy: user?.email || "system", override: false }],
      createdAt: now, createdBy: user?.email || "system", updatedAt: now,
    };
    try {
      const saved = await api.proposals.save(doc); // create path (generic upsert)
      setProposals((list) => [...list, saved || doc]);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message || "Create failed." };
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), // 5px so clicks still open the panel
    useSensor(KeyboardSensor),
  );

  const onDragEnd = ({ active, over }) => {
    if (over && active && over.id !== undefined) moveStage(active.id, String(over.id));
  };

  // Filter: OR within a category (any selected value matches), AND across categories.
  const filtered = useMemo(() => {
    const match = (vals, v) => vals.length === 0 || vals.includes((v || "").trim());
    return proposals.filter((p) =>
      match(filters.client, clientName(p)) &&
      match(filters.projectManager, p.projectManager) &&
      match(filters.accountOwner, p.accountOwner)
    );
  }, [proposals, filters, clientName]);

  const grouped = useMemo(() => {
    const g = {};
    for (const s of [...ACTIVE_STAGES, ...OUTCOME_STAGES]) g[s.value] = [];
    for (const p of filtered) { (g[p.stage] = g[p.stage] || []).push(p); }
    return g;
  }, [filtered]);

  const metrics = useMemo(() => computeMetrics(filtered), [filtered]);

  const filterOptions = useMemo(() => {
    const uniq = (key) => [...new Set(proposals.map((p) => (p[key] || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    const clientNames = [...new Set(proposals.map((p) => clientName(p)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    return { client: clientNames, projectManager: uniq("projectManager"), accountOwner: uniq("accountOwner") };
  }, [proposals, clientName]);

  const activeFilterCount = filters.client.length + filters.projectManager.length + filters.accountOwner.length;

  const selected = selectedId ? byId(selectedId) : null;

  if (loading) return <div style={{ padding: 40, color: colors.textMuted }}>Loading board…</div>;
  if (error) return (
    <div style={{ padding: 40 }}>
      <div style={{ color: colors.danger, marginBottom: 12 }}>Couldn’t load proposals: {error}</div>
      <button onClick={load} style={btn(colors.accent)}>Retry</button>
    </div>
  );

  const outcomeTotal = OUTCOME_STAGES.reduce((n, s) => n + grouped[s.value].length, 0);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: colors.text, letterSpacing: -0.3 }}>Proposal Pipeline</h1>
        <span style={{ fontSize: 13, color: colors.textDim }}>
          {activeFilterCount ? `${filtered.length} of ${proposals.length}` : proposals.length} proposals
        </span>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreating(true)} style={btn(colors.accent)}>+ New Proposal</button>
      </div>

      {/* Quickview metrics (respect active filters) */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <StatTile label="Pipeline Value" value={formatCurrency(metrics.pipelineValue)} color={colors.accent} />
        <StatTile label="Awaiting Response" value={metrics.awaiting} color="#67e8f9" />
        <StatTile label="Overdue" value={metrics.overdue} color={metrics.overdue > 0 ? colors.danger : colors.textDim} />
        <div style={{ width: 1, alignSelf: "stretch", background: colors.border, margin: "2px 4px" }} />
        <StatTile label="Proposals (YTD)" value={metrics.ytdTotal} color={colors.accent} />
        <StatTile label="Submitted Value (YTD)" value={formatCurrency(metrics.ytdValue)} color={colors.accent} />
        <StatTile label="Funded (YTD)" value={`${metrics.fundedCount} · ${formatCurrency(metrics.fundedValue)}`} color={colors.success} />
        <StatTile label="Win Rate" value={`${metrics.fundedRate}%`} color={metrics.fundedRate >= 50 ? colors.success : colors.warning} />
      </div>

      {/* Filter bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: colors.textDim, textTransform: "uppercase", letterSpacing: 1 }}>Filter</span>
        <MultiSelect label="Client" options={filterOptions.client} selected={filters.client} onChange={(v) => setFilters((f) => ({ ...f, client: v }))} />
        <MultiSelect label="Project Manager" options={filterOptions.projectManager} selected={filters.projectManager} onChange={(v) => setFilters((f) => ({ ...f, projectManager: v }))} />
        <MultiSelect label="Account Owner" options={filterOptions.accountOwner} selected={filters.accountOwner} onChange={(v) => setFilters((f) => ({ ...f, accountOwner: v }))} />
        {activeFilterCount > 0 && (
          <button onClick={() => setFilters(EMPTY_FILTERS)} style={{ ...btn("transparent"), color: colors.textMuted, border: `1px solid ${colors.border}` }}>Clear ({activeFilterCount})</button>
        )}
      </div>

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", overflowX: "auto", paddingBottom: 12 }}>
          {/* Active pipeline (primary group) */}
          {ACTIVE_STAGES.map((s) => (
            <Column key={s.value} stage={s.value} label={s.label} cards={grouped[s.value]} onOpen={setSelectedId} accent={colors.accent} clientName={clientName} />
          ))}

          {/* Divider + outcome group */}
          <div style={{ alignSelf: "stretch", width: 1, background: colors.borderLight, margin: "0 4px", flexShrink: 0 }} />
          <div style={{ flexShrink: 0 }}>
            <button onClick={toggleCollapsed} style={{ ...btn("transparent"), color: colors.textMuted, border: `1px solid ${colors.border}`, marginBottom: 8, width: collapsed ? 56 : "100%" }}>
              <span style={{ display: "inline-block", transform: collapsed ? "rotate(0deg)" : "rotate(90deg)", transition: "transform .15s" }}>▶</span>
              {collapsed ? ` ${outcomeTotal}` : ` Outcomes (${outcomeTotal})`}
            </button>
            {!collapsed && (
              <div style={{ display: "flex", gap: 12 }}>
                {OUTCOME_STAGES.map((s) => (
                  <Column key={s.value} stage={s.value} label={s.label} cards={grouped[s.value]} onOpen={setSelectedId} accent={OUTCOME_COLORS[s.value]} outcome clientName={clientName} />
                ))}
              </div>
            )}
          </div>
        </div>
      </DndContext>

      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 1100, maxWidth: 360, padding: "12px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600, color: "#fff",
          background: toast.type === "error" ? colors.danger : colors.success, boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}
          onClick={() => setToast(null)}>{toast.text}</div>
      )}

      {(selected || creating) && (
        <DetailPanel
          proposal={creating ? null : selected}
          users={users}
          admins={admins}
          clients={clients}
          isNew={creating}
          onSaveFields={(fields) => saveFields(selected.id, fields)}
          onSetStage={(toStage) => moveStage(selected.id, toStage)}
          onCreate={createProposal}
          onClose={() => { setSelectedId(null); setCreating(false); }}
        />
      )}
    </div>
  );
}

function Column({ stage, label, cards, onOpen, accent, outcome, clientName }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  return (
    <div ref={setNodeRef} style={{
      width: COL_W, flexShrink: 0, background: outcome ? "rgba(148,163,184,0.04)" : colors.surface,
      border: `1px solid ${isOver ? accent : colors.border}`, borderRadius: 12, display: "flex", flexDirection: "column",
      maxHeight: "calc(100vh - 220px)", transition: "border-color .12s",
    }}>
      <div style={{ padding: "12px 14px", borderBottom: `1px solid ${colors.border}`, display: "flex", alignItems: "center", gap: 8,
        borderTop: `3px solid ${accent}`, borderRadius: "12px 12px 0 0" }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: colors.text, letterSpacing: 0.3, textTransform: "uppercase", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: accent, background: `${accent}22`, borderRadius: 10, padding: "2px 8px", fontFeatureSettings: '"tnum"' }}>{cards.length}</span>
      </div>
      <div style={{ padding: 10, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, minHeight: 60 }}>
        {cards.length === 0
          ? <div style={{ fontSize: 12, color: colors.textDim, textAlign: "center", padding: "16px 0" }}>{isOver ? "Drop here" : "—"}</div>
          : cards.map((p) => <Card key={p.id} p={p} onOpen={onOpen} clientName={clientName} />)}
      </div>
    </div>
  );
}

function Card({ p, onOpen, clientName }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: p.id });
  const style = {
    background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 10, padding: "10px 12px",
    cursor: "grab", opacity: isDragging ? 0.4 : 1,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    boxShadow: isDragging ? "0 8px 24px rgba(0,0,0,0.4)" : "none",
  };
  const urgency = isPreSubmission(p.stage) ? dueUrgency(p.dueDate) : null;
  const u = urgency ? URGENCY_STYLE[urgency.level] : null;
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}
      onClick={() => { if (!isDragging) onOpen(p.id); }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, marginBottom: 4, lineHeight: 1.3 }}>{p.topic || "(untitled)"}</div>
      <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 6 }}>{(clientName ? clientName(p) : p.client) || "—"}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
        {p.branch && <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", background: colors.accentDim, borderRadius: 4, color: colors.accent }}>{p.branch}</span>}
        {u && <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4, color: u.color, background: u.bg, border: `1px solid ${u.border}` }}>{urgency.label}</span>}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, fontSize: 11, color: colors.textDim }}>
        <span>{p.projectManager || "unassigned"}</span>
        {Number(p.value) > 0 && <span style={{ fontFeatureSettings: '"tnum"', fontWeight: 600 }}>{formatCurrency(p.value)}</span>}
      </div>
    </div>
  );
}

function btn(bg) {
  return { padding: "9px 18px", background: bg, border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font };
}

function StatTile({ label, value, color }) {
  return (
    <div style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 10, padding: "10px 16px", minWidth: 120 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: colors.textDim, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color, fontFeatureSettings: '"tnum"', letterSpacing: -0.3 }}>{value}</div>
    </div>
  );
}
